import { useEffect, useRef, useState } from 'react'

/**
 * Purtarea barei modulului si a grilei de module, ca in
 * public/js/header_nav_reveal.js - aceleasi clase pe <body>, aceleasi
 * intarzieri, aceleasi chei in localStorage/sessionStorage, ca foile de stil
 * (header_nav_reveal.css) sa dea acelasi desen:
 *
 * - `nav-collapsible`: bara e stransa in numele sectiunii (`#current-section`)
 * - `nav-visible`: bara desfacuta (mouse pe nume sau pe bara)
 * - `nav-pinned`: prinsa deschisa, la clic pe nume; tine minte in localStorage
 * - `modules-shown`: grila de module, la mouse pe `#show-modules`
 */

const CLOSE_DELAY = 220
const AFTER_NAVIGATION_DELAY = 2200
const NAV_FLAG = 'headerNavStaysOpen'
const PIN_KEY = 'headerNavPinned'

function readPin(): boolean {
  try {
    return window.localStorage.getItem(PIN_KEY) === '1'
  } catch {
    return false
  }
}

function writePin(pinned: boolean): void {
  try {
    if (pinned) window.localStorage.setItem(PIN_KEY, '1')
    else window.localStorage.removeItem(PIN_KEY)
  } catch {
    /* fara memorie: se arata la trecerea cu mouse-ul si atat */
  }
}

/** Semnul lasat de un clic in bara: dupa navigare, bara mai sta putin deschisa. */
export function markNavigation(): void {
  try {
    window.sessionStorage.setItem(NAV_FLAG, '1')
  } catch {
    /* fara memorie: se inchide la navigare */
  }
}

function readFlag(): boolean {
  try {
    if (window.sessionStorage.getItem(NAV_FLAG)) {
      window.sessionStorage.removeItem(NAV_FLAG)
      return true
    }
  } catch {
    /* ca mai sus */
  }
  return false
}

export interface HeaderNavReveal {
  pinned: boolean
  /** Handlerele numelui sectiunii si ale barei. */
  open(): void
  scheduleClose(): void
  togglePin(): void
  /** Handlerele lui `#show-modules` si `#header-modules`. */
  showModules(): void
  hideModulesSoon(): void
}

export function useHeaderNavReveal(hasSection: boolean, routeKey: string): HeaderNavReveal {
  const [pinned, setPinned] = useState(readPin)
  const closeTimer = useRef<number | null>(null)
  const modulesTimer = useRef<number | null>(null)

  useEffect(() => {
    document.body.classList.toggle('nav-collapsible', hasSection)
    return () => {
      document.body.classList.remove('nav-collapsible', 'nav-visible', 'nav-pinned', 'modules-shown')
    }
  }, [hasSection])

  useEffect(() => {
    document.body.classList.toggle('nav-pinned', pinned)
    if (pinned) document.body.classList.add('nav-visible')
    else document.body.classList.remove('nav-visible')
  }, [pinned])

  const open = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
    document.body.classList.add('nav-visible')
  }

  const scheduleClose = (delay = CLOSE_DELAY) => {
    if (pinned) return
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => {
      document.body.classList.remove('nav-visible')
      closeTimer.current = null
    }, delay)
  }

  /* Dupa o navigare pornita din bara, bara mai sta deschisa cat sa se
     hotarasca omul daca vrea inca una. */
  useEffect(() => {
    if (readFlag() && !pinned) {
      open()
      scheduleClose(AFTER_NAVIGATION_DELAY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      document.body.classList.remove('modules-shown')
      if (!pinned) document.body.classList.remove('nav-visible')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [pinned])

  const showModules = () => {
    if (modulesTimer.current) {
      window.clearTimeout(modulesTimer.current)
      modulesTimer.current = null
    }
    document.body.classList.add('modules-shown')
  }

  const hideModulesSoon = () => {
    if (modulesTimer.current) window.clearTimeout(modulesTimer.current)
    modulesTimer.current = window.setTimeout(() => {
      document.body.classList.remove('modules-shown')
      modulesTimer.current = null
    }, CLOSE_DELAY)
  }

  return {
    pinned,
    open,
    scheduleClose: () => scheduleClose(),
    togglePin: () => {
      const next = !pinned
      setPinned(next)
      writePin(next)
    },
    showModules,
    hideModulesSoon,
  }
}
